import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import {
  selectBusinessConfig,
  selectBusinessConfigIsLoading,
  selectBusinessConfigHasLoaded,
  selectBusinessConfigError,
} from "../dataManagement/uiDb/BusinessConfigDb";
import { fetchBusinessConfig } from "../services/thunks/BusinessConfigThunks";
import { BusinessConfigData } from "../interfaces/BusinessObject";

interface UseBusinessConfigResult {
  /** `undefined` until the first successful fetch lands — NOT the same as
   *  `{}`, which is a valid "this business has configured nothing" result. */
  config: BusinessConfigData | undefined;
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  /** Forces a fresh fetch — e.g. right after a save, or a "Retry" button. A
   *  no-op when `businessId` isn't a real id yet. */
  refetch: () => void;
}

/**
 * A business's config, fetched once from the dedicated GET /config endpoint
 * and shared via BusinessConfigDb's cache — every consumer (the business
 * page, cart, the config editor, useBusinessSlotTemplate) uses this ONE hook
 * instead of each re-implementing "select by id, dispatch a fetch if
 * missing." No de-dupe guard is needed beyond what's already in the hook:
 * fetchBusinessConfig's own `condition` (see BusinessConfigThunks.ts) skips
 * a second dispatch while one for the same businessId is already in flight.
 *
 * `businessId` may be null/undefined (e.g. still resolving elsewhere) —
 * nothing is fetched or selected until it's a real id.
 */
export function useBusinessConfig(
  businessId: string | null | undefined
): UseBusinessConfigResult {
  const dispatch: AppDispatch = useDispatch();

  const config = useSelector((state: RootState) =>
    businessId ? selectBusinessConfig(state, businessId) : undefined
  );
  const isLoading = useSelector((state: RootState) =>
    businessId ? selectBusinessConfigIsLoading(state, businessId) : false
  );
  const hasLoaded = useSelector((state: RootState) =>
    businessId ? selectBusinessConfigHasLoaded(state, businessId) : false
  );
  const error = useSelector((state: RootState) =>
    businessId ? selectBusinessConfigError(state, businessId) : null
  );

  useEffect(() => {
    if (businessId && config === undefined) {
      dispatch(fetchBusinessConfig(businessId));
    }
  }, [businessId, config, dispatch]);

  const refetch = () => {
    if (businessId) dispatch(fetchBusinessConfig(businessId));
  };

  return { config, isLoading, hasLoaded, error, refetch };
}
